import React from 'react';
import type { LucideIcon } from 'lucide-react';
import Spinner from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  leftIcon?: LucideIcon;
  rightIcon?: LucideIcon;
  type?: 'button' | 'submit' | 'reset';
}

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-pt-forest text-pt-cream hover:bg-pt-forest-hover',
  secondary: 'bg-pt-paper text-pt-forest border border-default hover:bg-pt-cream',
  tertiary: 'bg-transparent text-pt-forest border border-default hover:bg-pt-cream',
  ghost: 'bg-transparent text-pt-forest hover:bg-pt-cream',
  danger: 'bg-error text-pt-cream hover:brightness-110',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-xs h-8 gap-1.5',
  md: 'px-4 py-2 text-sm h-10 gap-2',
  lg: 'px-5 py-2.5 text-md h-12 gap-2',
};

const ICON_SIZE: Record<ButtonSize, number> = { sm: 14, md: 16, lg: 18 };

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  type = 'button',
  disabled,
  className = '',
  children,
  onClick,
  ...rest
}) => {
  const isDisabled = disabled || loading;
  const base =
    'inline-flex items-center justify-center font-medium rounded-md transition-colors duration-[var(--motion-duration-fast)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pt-gold focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  const iconSize = ICON_SIZE[size];
  return (
    <button
      type={type}
      disabled={isDisabled}
      onClick={onClick}
      className={`${base} ${VARIANT[variant]} ${SIZE[size]} ${fullWidth ? 'w-full' : ''} ${className}`.trim()}
      {...rest}
    >
      {loading ? (
        <Spinner size={size === 'lg' ? 'md' : 'sm'} aria-label="Laden" />
      ) : (
        LeftIcon && <LeftIcon size={iconSize} strokeWidth={1.75} aria-hidden />
      )}
      {/* #1757 — tekst in een <span>: een browservertaler omwikkelt losse tekstnodes
          met <font>, en dan crasht React bij `loading` op removeChild. */}
      {!loading &&
        React.Children.map(children, (kind) =>
          typeof kind === 'string' || typeof kind === 'number' ? <span>{kind}</span> : kind
        )}
      {!loading && RightIcon && <RightIcon size={iconSize} strokeWidth={1.75} aria-hidden />}
    </button>
  );
};

export default Button;
